import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Nav, Row, Spinner, Table } from "react-bootstrap";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiChevronRight,
  FiPlus,
  FiSend,
  FiUsers,
  FiMessageSquare,
  FiShield,
  FiRefreshCw,
  FiClock,
  FiCalendar,
  FiTag,
  FiShoppingBag,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "../utilities/user";
import "./OmnichannelInbox.css";
import {
  apiFetchContactTags,
  apiFetchConsent,
  apiSetConsent,
  apiPreviewAudience,
  apiListCampaigns,
  apiCreateCampaign,
  apiGetCampaign,
  apiTestCampaign,
  apiLaunchCampaign,
  apiFetchWhatsAppTemplates,
} from "../api";
import AppSelect from "../components/ui/AppSelect";

// react-toastify is configured once by the inbox view, but configure() is
// idempotent so calling it here keeps Campaigns self-contained.
// Mirror the inbox's vendor resolution so a sub-account (staff) targets its
// parent vendor's WhatsApp assets.
const resolveVendorId = (profile) =>
  profile?.result?.parentVendorId || profile?.result?._id || profile?.result?.id;

// The vendor's own WhatsApp number — used to pre-fill the mandatory test send.
const resolveOwnPhone = (profile) => {
  const r = profile?.result || {};
  return r.phoneNumber || r.phone || r.mobile || r.contactNumber || "";
};

const fmt = (n) => Number(n || 0).toLocaleString();
const digitsOnly = (p) => String(p || "").replace(/\D/g, "");

// Pull the send-time off a campaign's schedule (Phase 2). The backend stores it
// under campaign.schedule.sendAt; fall back to a top-level scheduledFor/sendAt
// if the contract names it differently.
const scheduleSendAt = (campaign) =>
  campaign?.schedule?.sendAt || campaign?.scheduledFor || campaign?.sendAt || null;

const fmtDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
};

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in *local* time.
const toLocalInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

// Default the scheduler one hour out, rounded to the next 5 minutes.
const defaultScheduleValue = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
  return toLocalInputValue(d);
};

const prettyPhone = (p) => {
  const d = digitsOnly(p);
  if (d.length < 9) return p || "—";
  return `+${d}`;
};

// Map a campaign status → the tpl-status-pill modifier classes already styled
// in OmnichannelInbox.css (is-approved / is-pending / is-rejected).
const STATUS_PILL = {
  draft: "is-pending",
  scheduled: "is-pending",
  sending: "is-pending",
  paused: "is-pending",
  completed: "is-approved",
  cancelled: "is-rejected",
  failed: "is-rejected",
};

const StatusPill = ({ status }) => {
  const cls = STATUS_PILL[status] || "is-pending";
  return (
    <span className={`tpl-status-pill ${cls}`} style={{ textTransform: "capitalize" }}>
      {status || "draft"}
    </span>
  );
};

// Status pill + a "Scheduled for <time>" caption when a campaign is queued for a
// future send. Keeps the plain pill for every other status.
const CampaignStatus = ({ campaign = {}, block = false }) => {
  const at = campaign.status === "scheduled" ? scheduleSendAt(campaign) : null;
  const when = fmtDateTime(at);
  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: block ? "flex-start" : "flex-end",
        gap: 2,
      }}
    >
      <StatusPill status={campaign.status} />
      {when && (
        <span style={{ fontSize: 11, color: "#b45309", whiteSpace: "nowrap" }}>
          <FiClock style={{ marginRight: 3, verticalAlign: "-1px" }} />
          {when}
        </span>
      )}
    </div>
  );
};

// ── Reusable delivery funnel ───────────────────────────────────────────────
// Renders queued → sent → delivered → read, plus failed / skipped, from a
// stats object. Works for both list rows (compact) and the detail page.
const FUNNEL_STEPS = [
  { key: "queued", label: "Queued", color: "#64748b" },
  { key: "sent", label: "Sent", color: "#1d4ed8" },
  { key: "delivered", label: "Delivered", color: "#0e7490" },
  { key: "read", label: "Read", color: "#15803d" },
];

const SECONDARY_STEPS = [
  { key: "failed", label: "Failed", color: "#b91c1c" },
  { key: "skipped", label: "Skipped", color: "#b45309" },
];

const Funnel = ({ stats = {}, compact = false }) => {
  const cell = (label, value, color) => (
    <div
      key={label}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: compact ? 52 : 72,
      }}
    >
      <span style={{ fontWeight: 800, fontSize: compact ? 15 : 20, color }}>{fmt(value)}</span>
      <span style={{ fontSize: compact ? 10 : 12, color: "#94a3b8" }}>{label}</span>
    </div>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: compact ? 6 : 12 }}>
      {FUNNEL_STEPS.map((s, i) => (
        <React.Fragment key={s.key}>
          {cell(s.label, stats[s.key], s.color)}
          {i < FUNNEL_STEPS.length - 1 && (
            <FiChevronRight style={{ color: "#cbd5e1", flex: "0 0 auto" }} />
          )}
        </React.Fragment>
      ))}
      <div style={{ width: 1, alignSelf: "stretch", background: "#e2e8f0", margin: compact ? "0 2px" : "0 6px" }} />
      {SECONDARY_STEPS.map((s) => cell(s.label, stats[s.key], s.color))}
    </div>
  );
};

// Render a template body, substituting filled {{n}} variables for a live preview.
const renderPreview = (bodyText, vars) =>
  String(bodyText || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (m, n) => {
    const v = vars[Number(n) - 1];
    return v && v.trim() ? v : m;
  });

// ════════════════════════════════════════════════════════════════════════════
// Campaigns list
// ════════════════════════════════════════════════════════════════════════════
const CampaignsList = ({ vendorId, onOpen, onNew }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    (initial) => {
      if (!vendorId) return;
      if (initial) setLoading(true);
      apiListCampaigns(vendorId)
        .then(({ data }) => setCampaigns(data.campaigns || []))
        .catch((e) => setError(e.response?.data?.message || "Could not load campaigns."))
        .finally(() => initial && setLoading(false));
    },
    [vendorId]
  );

  useEffect(() => {
    load(true);
    // Light polling keeps the funnel "live" while a campaign is sending.
    const t = setInterval(() => load(false), 15000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <div className="text-center text-muted py-5">
        <Spinner animation="border" size="sm" /> Loading campaigns…
      </div>
    );
  }

  if (error) {
    return (
      <Card body className="text-center text-danger">
        {error}
        <div className="mt-2">
          <Button size="sm" variant="outline-secondary" onClick={() => load(true)}>
            <FiRefreshCw /> Retry
          </Button>
        </div>
      </Card>
    );
  }

  if (!campaigns.length) {
    return (
      <Card body className="text-center py-4" style={{ borderStyle: "dashed" }}>
        <FaWhatsapp size={32} style={{ color: "#25d366" }} />
        <h6 className="mt-3 mb-1" style={{ fontSize: 15, fontWeight: 700 }}>No campaigns yet</h6>
        <p className="text-muted mb-3" style={{ maxWidth: 440, margin: "0 auto", fontSize: 13 }}>
          A campaign sends one approved WhatsApp template to a tag-based audience of
          opted-in customers. Create your first one to reach customers at scale.
        </p>
        <Button className="omni-primary-action" onClick={onNew}>
          <FiPlus /> New campaign
        </Button>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body className="p-0">
        <Table responsive hover className="mb-0 align-middle">
          <thead>
            <tr style={{ fontSize: 12, color: "#64748b" }}>
              <th>Campaign</th>
              <th>Status</th>
              <th>Template</th>
              <th>Delivery funnel</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c._id} style={{ cursor: "pointer" }} onClick={() => onOpen(c._id)}>
                <td>
                  <div style={{ fontWeight: 700 }}>{c.name || "Untitled campaign"}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {c.createdAt ? new Date(c.createdAt).toLocaleString() : ""}
                  </div>
                </td>
                <td>
                  <CampaignStatus campaign={c} block />
                </td>
                <td style={{ fontSize: 13 }}>
                  <div>{c.templateName || "—"}</div>
                  <div className="text-muted" style={{ fontSize: 11 }}>{c.language || ""}</div>
                </td>
                <td>
                  <Funnel stats={c.stats} compact />
                </td>
                <td className="text-end">
                  <FiChevronRight style={{ color: "#94a3b8" }} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Campaign detail — funnel + recipient breakdown + sample list
// ════════════════════════════════════════════════════════════════════════════
const SKIP_LABEL = {
  opted_out: "Opted out",
  no_consent: "No consent",
  frequency_capped: "Frequency capped",
  invalid_phone: "Invalid number",
};

const CampaignDetail = ({ vendorId, campaignId, onBack }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    (initial) => {
      if (initial) setLoading(true);
      apiGetCampaign(vendorId, campaignId)
        .then(({ data }) => setDetail(data))
        .catch((e) => setError(e.response?.data?.message || "Could not load campaign."))
        .finally(() => initial && setLoading(false));
    },
    [vendorId, campaignId]
  );

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 10000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return (
      <div className="text-center text-muted py-5">
        <Spinner animation="border" size="sm" /> Loading campaign…
      </div>
    );
  }
  if (error || !detail) {
    return (
      <Card body className="text-center text-danger">
        {error || "Campaign not found."}
        <div className="mt-2">
          <Button size="sm" variant="outline-secondary" onClick={onBack}>
            <FiArrowLeft /> Back
          </Button>
        </div>
      </Card>
    );
  }

  const { campaign = {}, recipientCounts = {}, sample = [] } = detail;
  // Reconstruct a funnel-shaped stats object from recipientCounts (pending == queued).
  const funnelStats = {
    queued: recipientCounts.pending,
    sent: recipientCounts.sent,
    delivered: recipientCounts.delivered,
    read: recipientCounts.read,
    failed: recipientCounts.failed,
    skipped: recipientCounts.skipped,
  };
  const breakdown = [
    { key: "pending", label: "Pending", color: "#64748b" },
    { key: "sent", label: "Sent", color: "#1d4ed8" },
    { key: "delivered", label: "Delivered", color: "#0e7490" },
    { key: "read", label: "Read", color: "#15803d" },
    { key: "failed", label: "Failed", color: "#b91c1c" },
    { key: "skipped", label: "Skipped", color: "#b45309" },
  ];

  return (
    <div>
      <Button variant="link" className="px-0 mb-2" onClick={onBack}>
        <FiArrowLeft /> All campaigns
      </Button>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start flex-wrap" style={{ gap: 12 }}>
            <div>
              <h5 className="mb-1" style={{ fontSize: 18, fontWeight: 700 }}>{campaign.name || "Untitled campaign"}</h5>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Template <strong>{campaign.templateName}</strong>
                {campaign.language ? ` · ${campaign.language}` : ""}
              </div>
            </div>
            <CampaignStatus campaign={campaign} />
          </div>
          {campaign.status === "scheduled" && scheduleSendAt(campaign) && (
            <div
              className="mt-3 d-flex align-items-center"
              style={{
                gap: 8,
                background: "#fffbeb",
                border: "1px solid #fcd34d",
                borderRadius: 8,
                padding: "8px 12px",
                color: "#92400e",
                fontSize: 13,
              }}
            >
              <FiCalendar />
              Scheduled to send on <strong>{fmtDateTime(scheduleSendAt(campaign))}</strong>. It will
              start automatically at that time.
            </div>
          )}
          <hr />
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>Delivery funnel</div>
          <Funnel stats={funnelStats} />
        </Card.Body>
      </Card>

      <Row>
        <Col md={5} className="mb-3">
          <Card style={{ height: "100%" }}>
            <Card.Body>
              <h6 className="mb-3">Recipient breakdown</h6>
              <Table size="sm" className="mb-0">
                <tbody>
                  {breakdown.map((b) => (
                    <tr key={b.key}>
                      <td>
                        <span
                          style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: b.color,
                            marginRight: 8,
                          }}
                        />
                        {b.label}
                      </td>
                      <td className="text-end" style={{ fontWeight: 700 }}>
                        {fmt(recipientCounts[b.key])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        <Col md={7} className="mb-3">
          <Card style={{ height: "100%" }}>
            <Card.Body>
              <h6 className="mb-3">Sample recipients</h6>
              {sample.length === 0 ? (
                <div className="text-muted small">No recipients to show.</div>
              ) : (
                <Table size="sm" responsive className="mb-0 align-middle">
                  <thead>
                    <tr style={{ fontSize: 11, color: "#94a3b8" }}>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sample.map((r, i) => (
                      <tr key={`${r.phone}-${i}`}>
                        <td>{r.name || "—"}</td>
                        <td style={{ fontSize: 12 }}>{prettyPhone(r.phone)}</td>
                        <td style={{ textTransform: "capitalize", fontSize: 12 }}>{r.status || "—"}</td>
                        <td style={{ fontSize: 12, color: "#b45309" }}>
                          {r.skipReason ? SKIP_LABEL[r.skipReason] || r.skipReason : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// New-campaign wizard: Audience → Message → Review → (launch)
// ════════════════════════════════════════════════════════════════════════════
const STEPS = [
  { key: "audience", label: "Audience", icon: <FiUsers /> },
  { key: "message", label: "Message", icon: <FiMessageSquare /> },
  { key: "review", label: "Review & send", icon: <FiSend /> },
];

const Stepper = ({ step }) => (
  <div className="d-flex align-items-center mb-4" style={{ gap: 8, flexWrap: "wrap" }}>
    {STEPS.map((s, i) => {
      const active = i === step;
      const done = i < step;
      return (
        <React.Fragment key={s.key}>
          <div
            className="d-flex align-items-center"
            style={{
              gap: 6,
              padding: "6px 12px",
              borderRadius: 999,
              background: active ? "#dcfce7" : done ? "#f1f5f9" : "transparent",
              border: `1px solid ${active ? "#86efac" : "#e2e8f0"}`,
              color: active ? "#166534" : done ? "#0f766e" : "#94a3b8",
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {done ? <FiCheckCircle /> : s.icon}
            {s.label}
          </div>
          {i < STEPS.length - 1 && <FiChevronRight style={{ color: "#cbd5e1" }} />}
        </React.Fragment>
      );
    })}
  </div>
);

// Audience sources the wizard can target (Phase 2). Each builds a different
// `audience` object for the preview + create endpoints.
const AUDIENCE_SOURCES = [
  {
    key: "tag",
    label: "Contacts by tag",
    icon: <FiTag />,
    blurb: "Target everyone carrying one or more contact tags.",
  },
  {
    key: "orders",
    label: "Past customers",
    icon: <FiShoppingBag />,
    blurb: "Reach people who have ordered — with optional win-back targeting.",
  },
  {
    key: "chats",
    label: "Recent chats",
    icon: <FiMessageSquare />,
    blurb: "Re-engage contacts who messaged you recently.",
  },
];

const CampaignWizard = ({ vendorId, ownPhone, onCreated, onCancel }) => {
  const [step, setStep] = useState(0);

  // Step 1 — Audience
  const [allTags, setAllTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [name, setName] = useState("");

  // Step 1 — audience source + per-source inputs (Phase 2).
  const [audienceSource, setAudienceSource] = useState("tag"); // tag | orders | chats
  // Past customers
  const [orderWindowDays, setOrderWindowDays] = useState("90");
  const [minOrders, setMinOrders] = useState("1");
  const [winBackEnabled, setWinBackEnabled] = useState(false);
  const [notOrderedSinceDays, setNotOrderedSinceDays] = useState("60");
  // Recent chats
  const [chattedWithinDays, setChattedWithinDays] = useState("30");

  // Step 2 — Message
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [vars, setVars] = useState([]);

  // Step 3 — Review / launch
  const [createdCampaign, setCreatedCampaign] = useState(null);
  const [creating, setCreating] = useState(false);
  const [testPhone, setTestPhone] = useState(ownPhone || "");
  const [testing, setTesting] = useState(false);
  const [launching, setLaunching] = useState(false);

  // Step 3 — schedule (Phase 2). "now" launches immediately; "scheduled" defers
  // the send to a chosen local datetime.
  const [scheduleMode, setScheduleMode] = useState("now"); // now | scheduled
  const [scheduleAt, setScheduleAt] = useState(defaultScheduleValue);

  // Build the audience object from the chosen source + inputs. This single
  // definition drives both the live preview and the create call so they never
  // drift apart.
  const audience = useMemo(() => {
    if (audienceSource === "orders") {
      const a = { source: "past_orders" };
      const win = parseInt(orderWindowDays, 10);
      const min = parseInt(minOrders, 10);
      if (Number.isFinite(win) && win > 0) a.orderWindowDays = win;
      if (Number.isFinite(min) && min > 0) a.minOrders = min;
      if (winBackEnabled) {
        const since = parseInt(notOrderedSinceDays, 10);
        if (Number.isFinite(since) && since > 0) a.notOrderedSinceDays = since;
      }
      return a;
    }
    if (audienceSource === "chats") {
      const a = { source: "conversations" };
      const within = parseInt(chattedWithinDays, 10);
      if (Number.isFinite(within) && within > 0) a.chattedWithinDays = within;
      return a;
    }
    return { source: "contacts_tag", tags: selectedTags };
  }, [
    audienceSource,
    selectedTags,
    orderWindowDays,
    minOrders,
    winBackEnabled,
    notOrderedSinceDays,
    chattedWithinDays,
  ]);

  // Is the current audience definition complete enough to preview / send?
  const audienceReady =
    audienceSource === "tag"
      ? selectedTags.length > 0
      : audienceSource === "orders"
      ? !!audience.orderWindowDays || !!audience.notOrderedSinceDays
      : !!audience.chattedWithinDays;

  // Plain-English description of the audience for the Review summary.
  const audienceSummary = useMemo(() => {
    if (audience.source === "orders") {
      if (audience.notOrderedSinceDays) {
        return `Past customers who haven't ordered in ${audience.notOrderedSinceDays} days (win-back)`;
      }
      const parts = [];
      if (audience.minOrders) parts.push(`${audience.minOrders}+ orders`);
      if (audience.orderWindowDays) parts.push(`in the last ${audience.orderWindowDays} days`);
      return `Past customers${parts.length ? ` · ${parts.join(" ")}` : ""}`;
    }
    if (audience.source === "chats") {
      return `Recent chats · within ${audience.chattedWithinDays || "?"} days`;
    }
    return `Contacts by tag · ${selectedTags.join(", ") || "—"}`;
  }, [audience, selectedTags]);

  // Load tags up-front for the audience picker.
  useEffect(() => {
    if (!vendorId) return;
    setTagsLoading(true);
    apiFetchContactTags(vendorId)
      .then(({ data }) => setAllTags(data.tags || []))
      .catch(() => setAllTags([]))
      .finally(() => setTagsLoading(false));
  }, [vendorId]);

  // Live audience preview whenever the audience definition changes. Debounced so
  // typing into the numeric inputs doesn't fire a request per keystroke.
  const audienceKey = JSON.stringify(audience);
  useEffect(() => {
    if (!vendorId) return undefined;
    if (!audienceReady) {
      setPreview(null);
      return undefined;
    }
    let active = true;
    setPreviewLoading(true);
    const t = setTimeout(() => {
      apiPreviewAudience(vendorId, audience)
        .then(({ data }) => active && setPreview(data))
        .catch(() => active && setPreview(null))
        .finally(() => active && setPreviewLoading(false));
    }, 350);
    return () => {
      active = false;
      clearTimeout(t);
    };
    // audienceKey captures every field of `audience`; audienceReady gates it.
    // eslint-disable-next-line
  }, [vendorId, audienceKey, audienceReady]);

  const toggleTag = (tag) =>
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const loadTemplates = useCallback(() => {
    setTemplatesLoading(true);
    setTemplatesError("");
    apiFetchWhatsAppTemplates(vendorId)
      .then(({ data }) => {
        setTemplates(data.templates || []);
        if (data.reason) setTemplatesError(data.reason);
      })
      .catch((e) => setTemplatesError(e.response?.data?.message || "Could not load templates."))
      .finally(() => setTemplatesLoading(false));
  }, [vendorId]);

  const goToMessage = () => {
    if (templates.length === 0) loadTemplates();
    setStep(1);
  };

  const pickTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setVars(new Array(tpl?.variableCount || 0).fill(""));
  };

  // Create the draft, then advance to Review. Creating early gives us a real
  // campaign id so the "Send test" and "Launch" buttons hit the live endpoints.
  const createDraftAndReview = async () => {
    if (!selectedTemplate) return;
    setCreating(true);
    try {
      const { data } = await apiCreateCampaign(vendorId, {
        name: name.trim() || `${selectedTemplate.name} campaign`,
        audience,
        templateName: selectedTemplate.name,
        language: selectedTemplate.language,
        variables: vars,
      });
      setCreatedCampaign(data.campaign);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not create the campaign.");
    } finally {
      setCreating(false);
    }
  };

  const sendTest = async () => {
    if (!createdCampaign?._id || !digitsOnly(testPhone)) return;
    setTesting(true);
    try {
      const { data } = await apiTestCampaign(vendorId, createdCampaign._id, digitsOnly(testPhone));
      if (data.ok) toast.success("Test message sent to your number.");
      else toast.warn(data.reason || "Test could not be sent.");
    } catch (e) {
      toast.error(e.response?.data?.message || "Test send failed.");
    } finally {
      setTesting(false);
    }
  };

  const launch = async () => {
    if (!createdCampaign?._id) return;

    // Build the schedule payload. For a deferred send, convert the local
    // datetime-local value to an absolute ISO instant and guard against a
    // past time.
    let schedule;
    if (scheduleMode === "scheduled") {
      const when = scheduleAt ? new Date(scheduleAt) : null;
      if (!when || Number.isNaN(when.getTime())) {
        toast.warn("Pick a valid date and time to schedule this campaign.");
        return;
      }
      if (when.getTime() <= Date.now()) {
        toast.warn("Scheduled time must be in the future.");
        return;
      }
      schedule = { mode: "scheduled", sendAt: when.toISOString() };
    } else {
      schedule = { mode: "now", sendAt: new Date().toISOString() };
    }

    setLaunching(true);
    try {
      const { data } = await apiLaunchCampaign(vendorId, createdCampaign._id, schedule);
      toast.success(
        scheduleMode === "scheduled"
          ? `Campaign scheduled for ${fmtDateTime(schedule.sendAt)}.`
          : "Campaign launched."
      );
      onCreated(data.campaign?._id || createdCampaign._id);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not launch the campaign.");
    } finally {
      setLaunching(false);
    }
  };

  const varsFilled = vars.every((v) => v && v.trim());

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ fontSize: 16, fontWeight: 700 }}>New campaign</h5>
          <Button variant="link" className="text-muted px-0" onClick={onCancel}>
            Cancel
          </Button>
        </div>
        <Stepper step={step} />

        {/* ── Step 1 · Audience ─────────────────────────────────────────── */}
        {step === 0 && (
          <div>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">Campaign name</Form.Label>
              <Form.Control
                placeholder="e.g. Weekend promo"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Form.Group>

            <Form.Label className="fw-bold">Who gets this?</Form.Label>
            <p className="text-muted small mb-2">
              Choose how to build your audience. We resolve the matching contacts and exclude
              anyone who has not opted in, who opted out, or who was messaged too recently.
            </p>

            {/* Audience source picker */}
            <div className="d-flex flex-wrap mb-3" style={{ gap: 8 }}>
              {AUDIENCE_SOURCES.map((s) => {
                const on = audienceSource === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => {
                      setAudienceSource(s.key);
                      setPreview(null);
                    }}
                    style={{
                      flex: "1 1 160px",
                      textAlign: "left",
                      padding: "10px 12px",
                      borderRadius: 10,
                      cursor: "pointer",
                      background: on ? "#ecfdf5" : "#fff",
                      border: `1px solid ${on ? "#86efac" : "#e2e8f0"}`,
                    }}
                  >
                    <div
                      className="d-flex align-items-center"
                      style={{ gap: 6, fontWeight: 700, color: on ? "#166534" : "#0f172a" }}
                    >
                      {s.icon} {s.label}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {s.blurb}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* ── Source: Contacts by tag ─────────────────────────────────── */}
            {audienceSource === "tag" && (
              <div>
                <Form.Label className="small fw-bold mb-1">Contact tags</Form.Label>
                {tagsLoading ? (
                  <div className="text-muted small">
                    <Spinner animation="border" size="sm" /> Loading tags…
                  </div>
                ) : allTags.length === 0 ? (
                  <div className="text-muted small fst-italic">
                    No contact tags found. Tag contacts in Customer Chats first, then come back to
                    build an audience.
                  </div>
                ) : (
                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                    {allTags.map((tag) => {
                      const on = selectedTags.includes(tag);
                      return (
                        <Button
                          key={tag}
                          size="sm"
                          variant={on ? "success" : "outline-secondary"}
                          onClick={() => toggleTag(tag)}
                          style={{ borderRadius: 999 }}
                        >
                          {on ? <FiCheckCircle /> : null} {tag}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Source: Past customers ──────────────────────────────────── */}
            {audienceSource === "orders" && (
              <div>
                <Row>
                  <Col xs={6} md={4} className="mb-2">
                    <Form.Label className="small fw-bold mb-1">Ordered within (days)</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      placeholder="90"
                      value={orderWindowDays}
                      onChange={(e) => setOrderWindowDays(e.target.value)}
                      disabled={winBackEnabled}
                    />
                  </Col>
                  <Col xs={6} md={4} className="mb-2">
                    <Form.Label className="small fw-bold mb-1">Minimum orders</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      placeholder="1"
                      value={minOrders}
                      onChange={(e) => setMinOrders(e.target.value)}
                    />
                  </Col>
                </Row>
                <div
                  className="mt-2"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <Form.Check
                    type="switch"
                    id="winback-switch"
                    label="Win-back: target customers who have NOT ordered recently"
                    checked={winBackEnabled}
                    onChange={(e) => setWinBackEnabled(e.target.checked)}
                  />
                  {winBackEnabled && (
                    <div className="d-flex align-items-center mt-2" style={{ gap: 8 }}>
                      <span className="small text-muted">Haven't ordered in the last</span>
                      <Form.Control
                        type="number"
                        min={1}
                        size="sm"
                        style={{ width: 90 }}
                        value={notOrderedSinceDays}
                        onChange={(e) => setNotOrderedSinceDays(e.target.value)}
                      />
                      <span className="small text-muted">days</span>
                    </div>
                  )}
                  <div className="omni-microcopy mt-1">
                    {winBackEnabled
                      ? "Reaches lapsed customers to bring them back."
                      : "Reaches recent customers — combine with a minimum order count to target your best buyers."}
                  </div>
                </div>
              </div>
            )}

            {/* ── Source: Recent chats ────────────────────────────────────── */}
            {audienceSource === "chats" && (
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <span className="small fw-bold">Chatted within the last</span>
                <Form.Control
                  type="number"
                  min={1}
                  size="sm"
                  style={{ width: 100 }}
                  value={chattedWithinDays}
                  onChange={(e) => setChattedWithinDays(e.target.value)}
                />
                <span className="small text-muted">days</span>
              </div>
            )}

            {/* Live recipient count */}
            <div className="mt-3">
              {previewLoading ? (
                <div className="text-muted small">
                  <Spinner animation="border" size="sm" /> Counting recipients…
                </div>
              ) : preview ? (
                <Card body style={{ background: "#f8fafc" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#166534" }}>
                    {fmt(preview.sendable)} sendable
                  </div>
                  <div className="text-muted small">
                    {fmt(preview.optedOut)} opted-out · {fmt(preview.noConsent)} no-consent
                    {preview.frequencyCapped ? ` · ${fmt(preview.frequencyCapped)} recently-messaged excluded` : ""}
                    {" · "}
                    {fmt(preview.total)} matched in total
                  </div>
                  {Array.isArray(preview.sample) && preview.sample.length > 0 && (
                    <div className="small text-muted mt-2">
                      e.g. {preview.sample.slice(0, 3).map((s) => s.name || prettyPhone(s.phone)).join(", ")}
                      {preview.sample.length > 3 ? "…" : ""}
                    </div>
                  )}
                </Card>
              ) : audienceReady ? (
                <div className="text-muted small">No recipients matched this audience.</div>
              ) : null}
            </div>

            <div className="d-flex justify-content-end mt-4">
              <Button
                className="omni-primary-action"
                disabled={!audienceReady || !preview || !preview.sendable}
                onClick={goToMessage}
              >
                Next: Message <FiChevronRight />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2 · Message ──────────────────────────────────────────── */}
        {step === 1 && (
          <div>
            <Form.Label className="fw-bold">What do you want to say?</Form.Label>
            <p className="text-muted small mb-2">
              Campaigns send a pre-approved WhatsApp template. Pick one, fill its variables, and
              preview the exact message.
            </p>

            {templatesLoading ? (
              <div className="text-muted small">
                <Spinner animation="border" size="sm" /> Loading approved templates…
              </div>
            ) : templates.length === 0 ? (
              <div className="text-muted small fst-italic">
                {templatesError ||
                  "No approved templates available. Submit and get a template approved in Customer Chats first."}
              </div>
            ) : (
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, maxHeight: 240, overflowY: "auto" }}>
                {templates.map((tpl) => {
                  const on = selectedTemplate && selectedTemplate.name === tpl.name;
                  return (
                    <div
                      key={`${tpl.name}-${tpl.language}`}
                      onClick={() => pickTemplate(tpl)}
                      style={{
                        padding: "10px 12px",
                        borderBottom: "1px solid #f1f5f9",
                        cursor: "pointer",
                        background: on ? "#ecfdf5" : "transparent",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <strong style={{ fontSize: 14 }}>{tpl.name}</strong>
                        <span className="text-muted" style={{ fontSize: 11 }}>
                          {tpl.language}
                          {tpl.category ? ` · ${tpl.category.toLowerCase()}` : ""}
                        </span>
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>{tpl.bodyText}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedTemplate && (
              <div className="mt-3">
                {vars.length > 0 && (
                  <>
                    <Form.Label className="fw-bold">Fill the variables</Form.Label>
                    <p className="omni-microcopy mb-2">
                      Fill each blank that Meta inserts into the message ({"{{1}}, {{2}}…"}).
                    </p>
                    {vars.map((v, i) => (
                      <Form.Control
                        key={i}
                        className="mb-2"
                        placeholder={`Value for {{${i + 1}}}`}
                        value={v}
                        onChange={(e) => {
                          const next = [...vars];
                          next[i] = e.target.value;
                          setVars(next);
                        }}
                      />
                    ))}
                  </>
                )}

                {/* Live preview bubble */}
                <div className="mt-2">
                  <Form.Label className="fw-bold">Preview</Form.Label>
                  <div
                    style={{
                      background: "#dcf8c6",
                      borderRadius: "8px 8px 8px 2px",
                      padding: "10px 12px",
                      maxWidth: 420,
                      fontSize: 14,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {renderPreview(selectedTemplate.bodyText, vars)}
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-between mt-4">
              <Button variant="light" onClick={() => setStep(0)}>
                <FiArrowLeft /> Back
              </Button>
              <Button
                className="omni-primary-action"
                disabled={!selectedTemplate || !varsFilled || creating}
                onClick={createDraftAndReview}
              >
                {creating ? (
                  <>
                    <Spinner animation="border" size="sm" /> Preparing…
                  </>
                ) : (
                  <>
                    Next: Review <FiChevronRight />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3 · Review & send ────────────────────────────────────── */}
        {step === 2 && createdCampaign && (
          <div>
            <Row>
              <Col md={6} className="mb-3">
                <Card body style={{ height: "100%" }}>
                  <h6>Summary</h6>
                  <div className="small mb-1">
                    <span className="text-muted">Name: </span>
                    {createdCampaign.name}
                  </div>
                  <div className="small mb-1">
                    <span className="text-muted">Template: </span>
                    {createdCampaign.templateName} · {createdCampaign.language}
                  </div>
                  <div className="small mb-1">
                    <span className="text-muted">Audience: </span>
                    {audienceSummary}
                  </div>
                  <div className="small mt-2" style={{ fontSize: 18, fontWeight: 800, color: "#166534" }}>
                    {fmt(preview?.sendable)} recipients
                  </div>
                  <div className="text-muted small">
                    {fmt(preview?.optedOut)} opted-out · {fmt(preview?.noConsent)} no-consent
                    {preview?.frequencyCapped
                      ? ` · ${fmt(preview.frequencyCapped)} recently-messaged excluded`
                      : ""}
                  </div>
                </Card>
              </Col>
              <Col md={6} className="mb-3">
                <Card body style={{ height: "100%" }}>
                  <h6>Send a test to your number</h6>
                  <p className="text-muted small mb-2">
                    Always preview the real message on WhatsApp before launching.
                  </p>
                  <div className="d-flex" style={{ gap: 8 }}>
                    <Form.Control
                      size="sm"
                      placeholder="Your WhatsApp number"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="outline-success"
                      disabled={testing || !digitsOnly(testPhone)}
                      onClick={sendTest}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {testing ? <Spinner animation="border" size="sm" /> : <FiSend />} Send test
                    </Button>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* When to send (Phase 2) */}
            <Card body className="mb-3">
              <h6 className="mb-2">When should this go out?</h6>
              <div className="d-flex flex-wrap" style={{ gap: 16 }}>
                <Form.Check
                  type="radio"
                  id="schedule-now"
                  name="schedule-mode"
                  label="Send now"
                  checked={scheduleMode === "now"}
                  onChange={() => setScheduleMode("now")}
                />
                <Form.Check
                  type="radio"
                  id="schedule-later"
                  name="schedule-mode"
                  label="Schedule for later"
                  checked={scheduleMode === "scheduled"}
                  onChange={() => setScheduleMode("scheduled")}
                />
              </div>
              {scheduleMode === "scheduled" && (
                <div className="mt-3" style={{ maxWidth: 320 }}>
                  <Form.Label className="small fw-bold mb-1">
                    <FiCalendar style={{ marginRight: 4, verticalAlign: "-2px" }} />
                    Send date &amp; time
                  </Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={scheduleAt}
                    min={toLocalInputValue(new Date())}
                    onChange={(e) => setScheduleAt(e.target.value)}
                  />
                  <div className="omni-microcopy mt-1">
                    Uses your local time. The campaign stays in <em>Scheduled</em> until then, then
                    sends automatically.
                  </div>
                </div>
              )}
            </Card>

            <Card body style={{ background: "#fffbeb", borderColor: "#fcd34d" }}>
              <div className="small" style={{ color: "#92400e" }}>
                <FiShield /> Only contacts who have explicitly opted in to marketing will receive
                this message. Opted-out, no-consent, and recently-messaged contacts are
                automatically excluded.
              </div>
            </Card>

            <div className="d-flex justify-content-between mt-4">
              <Button variant="light" onClick={() => setStep(1)}>
                <FiArrowLeft /> Back
              </Button>
              <Button className="omni-primary-action" disabled={launching} onClick={launch}>
                {launching ? (
                  <>
                    <Spinner animation="border" size="sm" />{" "}
                    {scheduleMode === "scheduled" ? "Scheduling…" : "Launching…"}
                  </>
                ) : scheduleMode === "scheduled" ? (
                  <>
                    <FiClock /> Schedule campaign
                  </>
                ) : (
                  <>
                    <FiSend /> Send now
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Consent / opt-in panel
// ════════════════════════════════════════════════════════════════════════════
const ConsentPanel = ({ vendorId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [phone, setPhone] = useState("");
  const [mode, setMode] = useState("optIn"); // optIn | optOut
  const [source, setSource] = useState("manual_import");
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    (initial) => {
      if (!vendorId) return;
      if (initial) setLoading(true);
      apiFetchConsent(vendorId)
        .then(({ data }) => setData(data))
        .catch((e) => setError(e.response?.data?.message || "Could not load consent records."))
        .finally(() => initial && setLoading(false));
    },
    [vendorId]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const save = async () => {
    if (!digitsOnly(phone)) {
      toast.warn("Enter a valid phone number.");
      return;
    }
    setSaving(true);
    try {
      const payload =
        mode === "optIn"
          ? { phone: digitsOnly(phone), marketingOptIn: true, optedOut: false, source }
          : { phone: digitsOnly(phone), marketingOptIn: false, optedOut: true, source };
      await apiSetConsent(vendorId, payload);
      toast.success(mode === "optIn" ? "Marketing opt-in recorded." : "Opt-out recorded.");
      setPhone("");
      load(false);
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save consent.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md={4} className="mb-2">
          <Card body className="text-center">
            <div style={{ fontSize: 22, fontWeight: 800, color: "#15803d" }}>
              {loading ? "—" : fmt(data?.optedIn)}
            </div>
            <div className="text-muted small">Opted in to marketing</div>
          </Card>
        </Col>
        <Col md={4} className="mb-2">
          <Card body className="text-center">
            <div style={{ fontSize: 22, fontWeight: 800, color: "#b91c1c" }}>
              {loading ? "—" : fmt(data?.optedOut)}
            </div>
            <div className="text-muted small">Opted out</div>
          </Card>
        </Col>
        <Col md={4} className="mb-2">
          <Card body>
            <h6 className="mb-2">Record consent</h6>
            <Form.Control
              size="sm"
              className="mb-2"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <div className="d-flex mb-2" style={{ gap: 8 }}>
              <Form.Check
                type="radio"
                id="consent-optin"
                label="Opt in"
                checked={mode === "optIn"}
                onChange={() => setMode("optIn")}
              />
              <Form.Check
                type="radio"
                id="consent-optout"
                label="Opt out"
                checked={mode === "optOut"}
                onChange={() => setMode("optOut")}
              />
            </div>
            <AppSelect block
              size="sm"
              className="mb-2"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              <option value="manual_import">Manual import</option>
              <option value="order_checkout">Order checkout</option>
              <option value="chat_keyword">Chat keyword</option>
              <option value="inbound_message">Inbound message</option>
            </AppSelect>
            <Button size="sm" className="omni-primary-action w-100" disabled={saving} onClick={save}>
              {saving ? <Spinner animation="border" size="sm" /> : "Save"}
            </Button>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center text-muted py-4">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : error ? (
            <div className="text-center text-danger py-4">{error}</div>
          ) : !data?.records?.length ? (
            <div className="text-center text-muted py-4 small">
              No consent records yet. Record a customer's marketing opt-in above so campaigns have
              an audience.
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr style={{ fontSize: 12, color: "#64748b" }}>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map((r, i) => (
                  <tr key={`${r.phone}-${i}`}>
                    <td>{r.name || "—"}</td>
                    <td style={{ fontSize: 13 }}>{prettyPhone(r.phone)}</td>
                    <td>
                      {r.optedOut ? (
                        <Badge bg="danger">Opted out</Badge>
                      ) : r.marketingOptIn ? (
                        <Badge bg="success">Opted in</Badge>
                      ) : (
                        <Badge bg="secondary">No consent</Badge>
                      )}
                    </td>
                    <td className="small text-muted">{r.optInSource || "—"}</td>
                    <td className="small text-muted">
                      {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// Top-level view: tabs for Campaigns / New campaign / Consent
// ════════════════════════════════════════════════════════════════════════════
const Campaigns = () => {
  const profile = useMemo(() => getUserFromLocalStorage(), []);
  const vendorId = resolveVendorId(profile);
  const ownPhone = resolveOwnPhone(profile);

  const [tab, setTab] = useState("campaigns"); // campaigns | new | consent
  const [openId, setOpenId] = useState(null); // when set, show CampaignDetail

  if (!vendorId) {
    return (
      <Container fluid className="py-4">
        <Card body className="text-center text-muted">
          Could not resolve your vendor account. Please sign in again.
        </Card>
      </Container>
    );
  }

  const openCampaign = (id) => {
    setOpenId(id);
    setTab("campaigns");
  };

  return (
    <Container fluid className="py-3">
      <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
        <FaWhatsapp size={20} style={{ color: "#25d366" }} />
        <h5 className="mb-0" style={{ fontWeight: 700, fontSize: 18 }}>WhatsApp Campaigns</h5>
      </div>

      <Nav variant="tabs" activeKey={tab} className="mb-3">
        <Nav.Item>
          <Nav.Link
            eventKey="campaigns"
            onClick={() => {
              setTab("campaigns");
              setOpenId(null);
            }}
          >
            Campaigns
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            eventKey="new"
            onClick={() => {
              setTab("new");
              setOpenId(null);
            }}
          >
            <FiPlus /> New campaign
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="consent" onClick={() => setTab("consent")}>
            <FiShield /> Consent
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {tab === "campaigns" &&
        (openId ? (
          <CampaignDetail vendorId={vendorId} campaignId={openId} onBack={() => setOpenId(null)} />
        ) : (
          <CampaignsList vendorId={vendorId} onOpen={openCampaign} onNew={() => setTab("new")} />
        ))}

      {tab === "new" && (
        <CampaignWizard
          vendorId={vendorId}
          ownPhone={ownPhone}
          onCreated={openCampaign}
          onCancel={() => setTab("campaigns")}
        />
      )}

      {tab === "consent" && <ConsentPanel vendorId={vendorId} />}
    </Container>
  );
};

export default Campaigns;
